import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  TextField,
  Paper,
  Chip,
  Autocomplete,
  CircularProgress,
} from '@leap4/frontend';
import { MuiIcons } from '@leap4/frontend';
import { IReportFilter, IFilterOptions, IFilterOption } from '../report.interfaces';
import { FILTER_FIELDS } from '../report.constants';

const { Delete, Add, ExpandMore, ChevronRight } = MuiIcons;

interface FiltersSectionProps {
  filters: IReportFilter[];
  onAddFilter: () => void;
  onRemoveFilter: (id: number) => void;
  onUpdateFilter: (id: number, key: keyof IReportFilter, value: any) => void;
  filterOptions?: IFilterOptions;
  filterOptionsLoading?: boolean;
}

export const FiltersSection: React.FC<FiltersSectionProps> = ({
  filters,
  onAddFilter,
  onRemoveFilter,
  onUpdateFilter,
  filterOptions,
  filterOptionsLoading = false,
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const getOptionsForField = useMemo(() => {
    return (fieldKey: string): IFilterOption[] => {
      if (!filterOptions) return [];
      return filterOptions[fieldKey as keyof IFilterOptions] || [];
    };
  }, [filterOptions]);

  return (
    <Box>
      <Box
        onClick={() => setIsOpen(!isOpen)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          userSelect: 'none',
        }}
      >
        {isOpen ? (
          <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
        ) : (
          <ChevronRight sx={{ fontSize: 20, color: 'text.secondary' }} />
        )}
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: 11,
          }}
        >
          Filters
        </Typography>
        {filters.length > 0 && (
          <Chip
            label={filters.length}
            size="small"
            color="primary"
            sx={{ height: 20, fontSize: 11, ml: 0.5 }}
          />
        )}
      </Box>

      <Collapse in={isOpen}>
        <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}>
          {filters.map((filter) => (
            <Paper
              key={filter.id}
              elevation={0}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'action.hover',
                width: '100%',
                maxWidth: 700,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" fontWeight={500}>Filter</Typography>
                <IconButton
                  size="small"
                  onClick={() => onRemoveFilter(filter.id)}
                  sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <Select
                    value={filter.field || ''}
                    onChange={(e) => onUpdateFilter(filter.id, 'field', e.target.value)}
                    displayEmpty
                    sx={{ borderRadius: 1 }}
                    renderValue={(selected) => {
                      if (!selected) return <Typography color="text.secondary">Select field</Typography>;
                      return FILTER_FIELDS.find((f) => f.key === selected)?.label || selected;
                    }}
                  >
                    {FILTER_FIELDS.map((f) => (
                      <MenuItem key={f.key} value={f.key}>
                        {f.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <ToggleButtonGroup
                  value={filter.mode}
                  exclusive
                  onChange={(_, value) => value && onUpdateFilter(filter.id, 'mode', value)}
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      borderRadius: 1,
                      px: 2,
                      py: 0.5,
                      fontSize: 13,
                      textTransform: 'none',
                    },
                  }}
                >
                  <ToggleButton
                    value="include"
                    sx={{
                      '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } },
                    }}
                  >
                    Include
                  </ToggleButton>
                  <ToggleButton
                    value="exclude"
                    sx={{
                      '&.Mui-selected': { bgcolor: 'grey.500', color: 'white', '&:hover': { bgcolor: 'grey.600' } },
                    }}
                  >
                    Exclude
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>

              {filter.field && (
                <Autocomplete
                  multiple
                  size="small"
                  options={getOptionsForField(filter.field)}
                  loading={filterOptionsLoading}
                  value={filter.values.map((v) => {
                    const options = getOptionsForField(filter.field);
                    return options.find((opt) => opt.id === v || opt.name === v) || { id: v, name: v };
                  })}
                  onChange={(_, newValue) => {
                    onUpdateFilter(filter.id, 'values', newValue.map((v) => v.id));
                  }}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options, { inputValue }) => {
                    const searchTerm = inputValue.toLowerCase().trim();
                    if (!searchTerm) return options;
                    return options.filter((option) =>
                      option.name.toLowerCase().includes(searchTerm) ||
                      option.id.toLowerCase().includes(searchTerm)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={`Search ${FILTER_FIELDS.find((f) => f.key === filter.field)?.label || ''}...`}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1,
                          bgcolor: 'background.paper',
                        },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {filterOptionsLoading && <CircularProgress color="inherit" size={20} />}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                  noOptionsText={filterOptionsLoading ? 'Loading options...' : 'No options found'}
                  disableCloseOnSelect
                  sx={{ width: '100%' }}
                />
              )}
            </Paper>
          ))}

          <Button
            variant="outlined"
            size="small"
            color="primary"
            startIcon={<Add />}
            onClick={onAddFilter}
            sx={{ 
              borderStyle: 'dashed',
              borderRadius: 1,
              textTransform: 'none',
              px: 2,
            }}
          >
            Add Filter
          </Button>
        </Box>
      </Collapse>
    </Box>
  );
};
